/**
 * "Cargo Ledger" — shared design tokens for the admin Commission flows.
 * Editorial operations console aesthetic: warm paper, deep ink,
 * single oxidized-green accent, hairline dividers, monospace data.
 */

export const INK = {
  bg: '#FAF7F2',
  surface: '#FFFFFF',
  surfaceTint: '#FDFBF6',
  text: '#0F0F0F',
  muted: '#5C5A52',
  subtle: '#8E8B82',
  faint: '#B8B5AA',
  hairline: '#E8E3D6',
  hairlineSoft: '#F0EBDE',
  accent: '#1F4E3D',
  accentSoft: '#EEF3EF',
  accentHover: '#173E30',
  active: '#1F4E3D',
  inactive: '#A03A2A',
  inactiveSoft: '#FBEAE6',
  warning: '#B8651B',
  amberSoft: '#FBF1E0',
};

export const FONTS = {
  serif: '"Instrument Serif", Georgia, "Times New Roman", serif',
  sans: '"Inter", system-ui, -apple-system, sans-serif',
  mono: '"JetBrains Mono", "SF Mono", Menlo, monospace',
};

/* ─── BUTTON STYLES ─── */
export const primaryBtnSx = {
  bgcolor: INK.accent,
  color: INK.surface,
  fontFamily: FONTS.sans,
  fontWeight: 500,
  fontSize: 13,
  letterSpacing: '-0.005em',
  textTransform: 'none' as const,
  borderRadius: 0.5,
  px: 2.25,
  py: 1.1,
  boxShadow: 'none',
  border: `1px solid ${INK.accent}`,
  '&:hover': { bgcolor: INK.accentHover, boxShadow: 'none', borderColor: INK.accentHover },
  '&.Mui-disabled': { bgcolor: INK.hairlineSoft, color: INK.faint, borderColor: INK.hairline },
};

export const ghostBtnSx = {
  bgcolor: 'transparent',
  color: INK.text,
  fontFamily: FONTS.sans,
  fontWeight: 500,
  fontSize: 13,
  letterSpacing: '-0.005em',
  textTransform: 'none' as const,
  borderRadius: 0.5,
  px: 2,
  py: 1.05,
  border: `1px solid ${INK.hairline}`,
  '&:hover': { bgcolor: INK.surfaceTint, borderColor: INK.text },
  '&.Mui-disabled': { color: INK.faint, borderColor: INK.hairlineSoft },
};

export const dangerBtnSx = {
  bgcolor: INK.inactive,
  color: INK.surface,
  fontFamily: FONTS.sans,
  fontWeight: 500,
  fontSize: 13,
  textTransform: 'none' as const,
  borderRadius: 0.5,
  px: 2.25,
  py: 1.1,
  boxShadow: 'none',
  '&:hover': { bgcolor: '#882F22', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: INK.hairlineSoft, color: INK.faint },
};

/* ─── INPUTS ─── */
export const ledgerInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0.5,
    fontFamily: FONTS.sans,
    fontSize: 13,
    bgcolor: INK.surface,
    '& fieldset': { borderColor: INK.hairline },
    '&:hover fieldset': { borderColor: INK.muted },
    '&.Mui-focused fieldset': { borderColor: INK.accent, borderWidth: 1 },
  },
  '& .MuiInputLabel-root': {
    fontFamily: FONTS.sans,
    fontSize: 13,
    color: INK.muted,
    '&.Mui-focused': { color: INK.accent },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: FONTS.sans,
    fontSize: 11,
    color: INK.subtle,
    ml: 0.5,
  },
  '& .MuiInputBase-input::placeholder': { color: INK.subtle, opacity: 1 },
  '& .MuiInputBase-input': { fontFamily: FONTS.sans },
};

export const ledgerSelectSx = {
  borderRadius: 0.5,
  fontFamily: FONTS.sans,
  fontSize: 13,
  bgcolor: INK.surface,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: INK.hairline },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: INK.muted },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: INK.accent, borderWidth: 1 },
};

/* ─── CHIPS ─── */
export const ledgerChipSx = {
  bgcolor: INK.accentSoft,
  color: INK.accent,
  border: `1px solid ${INK.accent}`,
  borderRadius: 0.5,
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 500,
  height: 22,
  '& .MuiChip-deleteIcon': { color: INK.accent, fontSize: 14, '&:hover': { color: INK.accentHover } },
};

export const neutralChipSx = {
  bgcolor: INK.surfaceTint,
  color: INK.text,
  border: `1px solid ${INK.hairline}`,
  borderRadius: 0.5,
  fontFamily: FONTS.mono,
  fontSize: 11,
  fontWeight: 500,
  height: 22,
  letterSpacing: '0.04em',
  '& .MuiChip-deleteIcon': { color: INK.muted, fontSize: 14, '&:hover': { color: INK.text } },
};

/* ─── DIALOG TITLE ─── */
export const editorialDialogTitleSx = {
  fontFamily: FONTS.serif,
  fontSize: 28,
  letterSpacing: '-0.015em',
  color: INK.text,
  fontWeight: 400,
  borderBottom: `1px solid ${INK.hairlineSoft}`,
  py: 2.5,
};

/* ─── SWITCH ─── */
export const ledgerSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: INK.accent },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: INK.accent },
};

/* ─── SECTION HEADER (numbered, serif) ─── */
export const sectionEyebrowSx = {
  fontFamily: FONTS.mono,
  fontSize: 10,
  letterSpacing: '0.18em',
  color: INK.accent,
  textTransform: 'uppercase' as const,
  fontWeight: 500,
};

export const sectionTitleSx = {
  fontFamily: FONTS.serif,
  fontSize: 28,
  lineHeight: 1.1,
  letterSpacing: '-0.015em',
  color: INK.text,
  fontWeight: 400,
};

/* ─── FIELD LABEL (small caps mono) ─── */
export const fieldLabelSx = {
  fontFamily: FONTS.mono,
  fontSize: 10,
  letterSpacing: '0.16em',
  color: INK.subtle,
  textTransform: 'uppercase' as const,
  fontWeight: 500,
  mb: 1,
  display: 'block' as const,
};
